/** t352u: phone Settings › Equipment categories (desktop features/ops/EquipmentCategoriesPanel). Add, rename, reorder, archive. */
import { useState } from 'react';
import { Archive, ArrowDown, ArrowUp } from 'lucide-react';
import { EQUIPMENT_CATEGORIES_NOTE, equipmentCategoryGroups, equipmentCategoryOrder, equipmentCategoryProblem, type EquipmentCategory } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Spinner, ask } from '@/components/ui';
import { opsKeys } from '@/features/ops/common';
import { useEquipmentCategories } from '@/features/ops/EquipmentCategoriesPanel';
import { BottomSheet } from '../BottomSheet';
import { F, ghostBtn, saveBtn } from './PersonSettings';
import { listCard, note, WsAddRow, WsHead, WsRow } from './WorkspaceSettings';

const count = (c: EquipmentCategory) => `${c.item_count} item${c.item_count === 1 ? '' : 's'}`;

export function EquipmentCategoriesPage() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useEquipmentCategories();
  const all = q.data ?? [];
  const g = equipmentCategoryGroups(all);
  const [open, setOpen] = useState<{ c: EquipmentCategory | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast(ok, 'success'); void queryClient.invalidateQueries({ queryKey: opsKeys(ws ?? '', 'equipment') }); return true; }
    catch (e) { toast(errorMessage(e, 'Couldn’t save the category'), 'error'); return false; }
    finally { setBusy(false); }
  };
  const move = (id: string, by: -1 | 1) => void run(() => api.ops.reorderEquipmentCategories(ws!, equipmentCategoryOrder(all, id, by)), 'Order saved');
  const arrow = 'flex h-11 w-11 items-center justify-center text-slate-600 disabled:opacity-30 dark:text-slate-300';
  const row = (c: EquipmentCategory, i?: number) => (
    <WsRow key={c.id} testId="m-eqcat-row" title={c.name} line={c.archived_at ? `${count(c)} · archived` : count(c)} onClick={() => setOpen({ c })}
      right={i !== undefined && g.live.length > 1 ? <span className="flex shrink-0 pr-1">
        <button type="button" className={arrow} aria-label={`Move ${c.name} up`} disabled={busy || i === 0} onClick={() => move(c.id, -1)}><ArrowUp className="h-5 w-5" /></button>
        <button type="button" className={arrow} aria-label={`Move ${c.name} down`} disabled={busy || i === g.live.length - 1} onClick={() => move(c.id, 1)}><ArrowDown className="h-5 w-5" /></button>
      </span> : undefined} />
  );
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-eqcats">
      <p className={note}>{EQUIPMENT_CATEGORIES_NOTE}</p>
      <div className={`${listCard} mt-3`} data-testid="m-eqcats-list">
        {q.isLoading ? <div className="p-4"><Spinner /></div> : !g.live.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No categories yet.</p>}
        {g.live.map((c, i) => row(c, i))}
        <WsAddRow label="Add a category" onClick={() => setOpen({ c: null })} testId="m-eqcats-add" />
      </div>
      {g.archived.length > 0 && <><WsHead>Archived</WsHead><div className={listCard} data-testid="m-eqcats-archived">{g.archived.map(c => row(c))}</div></>}
      {open && <CategorySheet c={open.c} all={all} busy={busy} onClose={() => setOpen(null)}
        onSave={name => run(() => (open.c ? api.ops.updateEquipmentCategory(ws!, open.c.id, { name }) : api.ops.createEquipmentCategory(ws!, { name })), open.c ? 'Category saved' : 'Category added')}
        onArchive={open.c ? async () => {
          const c = open.c!;
          if (c.archived_at) return run(() => api.ops.updateEquipmentCategory(ws!, c.id, { archived: false }), `${c.name} restored`);
          return (await ask({ title: `Archive “${c.name}”?`, body: c.item_count ? `Its ${count(c)} keep it, but it can’t be picked for other equipment.` : 'It can’t be picked any more. You can restore it later.', confirmLabel: 'Archive' }))
            ? run(() => api.ops.updateEquipmentCategory(ws!, c.id, { archived: true }), `${c.name} archived`) : false;
        } : undefined} />}
    </div>
  );
}

function CategorySheet({ c, all, busy, onClose, onSave, onArchive }: { c: EquipmentCategory | null; all: EquipmentCategory[]; busy: boolean; onClose: () => void; onSave: (name: string) => Promise<boolean>; onArchive?: () => Promise<boolean> }) {
  const [name, setName] = useState(c?.name ?? '');
  const problem = c?.archived_at ? null : equipmentCategoryProblem(name, all, c?.id);
  return (
    <BottomSheet open onClose={onClose} title={c ? `Edit ${c.name}` : 'Add a category'} testId="m-eqcats-sheet"
      footer={<div className="flex gap-2 px-3 py-2">
        {onArchive && <button type="button" className={ghostBtn} disabled={busy} onClick={async () => { if (await onArchive()) onClose(); }} data-testid="m-eqcats-archive">
          <Archive className="h-5 w-5" />{c?.archived_at ? 'Restore' : 'Archive'}</button>}
        {!c?.archived_at && <button type="button" className={saveBtn} disabled={busy || Boolean(problem)} onClick={async () => { if (await onSave(name.trim())) onClose(); }} data-testid="m-eqcats-save">{c ? 'Save' : 'Add category'}</button>}
      </div>}>
      <div className="space-y-1 px-4 pb-2">
        <F label="Name"><Input value={name} maxLength={60} placeholder="e.g. Cleaning machines" disabled={Boolean(c?.archived_at)} onChange={e => setName(e.target.value)} data-testid="m-eqcats-name" /></F>
        {name.trim() && problem && <p className="text-sm text-rose-700 dark:text-rose-300" role="status">{problem}</p>}
        {c && <p className="text-sm text-slate-500">{c.archived_at ? `Archived. Its ${count(c)} keep it; restore it to pick it again.` : `${count(c)} in this category.`}</p>}
      </div>
    </BottomSheet>
  );
}
